import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  deviceByKeyQueryOptions,
  deviceOverridesByDeviceKeyQueryOptions,
  updateDeviceOverridesMutateOptions,
} from '../../../queryOptions';
import {
  Device,
  DeviceOverrides,
  DeviceOverridesRequest,
  UpdateRequest,
} from '../../../models';
import {
  DeviceStatus,
  studyIdSchema,
  siteIdSchema,
  subjectIdSchema,
} from 'adc-sirius-module-common';
import { Card, VStack } from '@adc/parallax-component-library';
import DynamicForm, {
  DynamicFormConfig,
  DynamicFormFieldType,
} from '../../form/DynamicForm';
import * as Yup from 'yup';
import i18n from '../../../utilities/i18n';
import { enumToChoiceOptions } from '../../../utilities/helpers';
import { getErrorMessageOnEntityCreateUpdate } from '../../../utilities/crudHelpers';

type DeviceHeaderProps = {
  device: Device;
  deviceOverrides: DeviceOverrides;
  resolvedHierarchyNames?: {
    organizationName?: string;
    studyName?: string;
    siteName?: string;
    subjectName?: string;
  };
  children?: React.ReactNode;
};

type DeviceFormValues = Omit<Device, 'status'> &
  UpdateRequest & {
    status: string; // converted to string for dropdown input
  };

/**
 * DeviceHeader component displays the header information for a specific device.
 * @param {Device} device - The device object containing details to display.
 * @param {DeviceOverrides} deviceOverrides - The device override values.
 * @param {Object} resolvedHierarchyNames - Optional resolved display names for hierarchy entities.
 * @param {React.JSX.Element} children - The child components to render within the header.
 * @returns {React.JSX.Element}
 */
export default function DeviceHeader({
  device,
  deviceOverrides,
  resolvedHierarchyNames,
  children,
}: DeviceHeaderProps) {
  const queryClient = useQueryClient();

  const overrideValuesValidationSchema = {
    studyKeyOverride: Yup.string()
      .matches(studyIdSchema.matcher, {
        message: studyIdSchema.allowedSpecialCharacters.length
          ? i18n.t('Form.allowedSpecialCharacters', {
              characters: studyIdSchema.allowedSpecialCharacters.join(' '),
            })
          : i18n.t('Form.allowedSpecialCharactersEmpty'),
        excludeEmptyString: true,
      })
      .test(
        'len',
        () =>
          i18n.t('Form.genericMaxLength', {
            count: studyIdSchema.maxLength,
          }),
        (val) =>
          studyIdSchema.maxLength && val
            ? val.length <= studyIdSchema.maxLength
            : true,
      ),
    siteKeyOverride: Yup.string()
      .matches(siteIdSchema.matcher, {
        message: siteIdSchema.allowedSpecialCharacters.length
          ? i18n.t('Form.allowedSpecialCharacters', {
              characters: siteIdSchema.allowedSpecialCharacters.join(' '),
            })
          : i18n.t('Form.allowedSpecialCharactersEmpty'),
        excludeEmptyString: true,
      })
      .test(
        'len',
        () =>
          i18n.t('Form.genericMaxLength', {
            count: siteIdSchema.maxLength,
          }),
        (val) =>
          siteIdSchema.maxLength && val
            ? val.length <= siteIdSchema.maxLength
            : true,
      ),
    subjectKeyOverride: Yup.string()
      .matches(subjectIdSchema.matcher, {
        message: subjectIdSchema.allowedSpecialCharacters.length
          ? i18n.t('Form.allowedSpecialCharacters', {
              characters: subjectIdSchema.allowedSpecialCharacters.join(' '),
            })
          : i18n.t('Form.allowedSpecialCharactersEmpty'),
        excludeEmptyString: true,
      })
      .test(
        'len',
        () =>
          i18n.t('Form.genericMaxLength', {
            count: subjectIdSchema.maxLength,
          }),
        (val) =>
          subjectIdSchema.maxLength && val
            ? val.length <= subjectIdSchema.maxLength
            : true,
      ),
  };

  const mapFormValuesToDeviceOverrides = (
    values: DeviceFormValues,
  ): DeviceOverridesRequest => {
    const { newUpdateReason, ...updatedValues } = values;

    const deviceOverridesPayload = {
      ...deviceOverrides,
      updateReason: newUpdateReason,
    };

    for (const key of Object.keys(updatedValues) as Array<
      keyof DeviceFormValues
    >) {
      if (Object.keys(deviceOverrides).includes(key)) {
        deviceOverridesPayload[key] = updatedValues[key];
      }
    }

    return deviceOverridesPayload;
  };

  const mapDeviceAndDeviceOverridesToFormValues = (
    device: Device,
    deviceOverrides: DeviceOverrides,
  ): DeviceFormValues => ({
    ...device,
    ...deviceOverrides,
    status: String(device.status),
    organizationKey:
      resolvedHierarchyNames?.organizationName || device.organizationKey,
    studyKey: resolvedHierarchyNames?.studyName || device.studyKey,
    siteKey: resolvedHierarchyNames?.siteName || device.siteKey,
    subjectKey: resolvedHierarchyNames?.subjectName || device.subjectKey,
    newUpdateReason: '',
  });

  const formConfig: DynamicFormConfig<DeviceFormValues> = {
    fields: [
      {
        name: 'appName',
        label: i18n.t('Page_Device.Form.appName'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'deviceId',
        label: i18n.t('Page_Device.Form.deviceId'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'status',
        label: i18n.t('Page_Device.Form.status'),
        type: DynamicFormFieldType.CHOICE,
        isEditable: false,
        choiceOptions: enumToChoiceOptions(DeviceStatus, 'DeviceStatus'),
      },
      {
        name: 'organizationKey',
        label: i18n.t('Page_Device.Form.organization'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'studyKey',
        label: i18n.t('Page_Device.Form.study'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'siteKey',
        label: i18n.t('Page_Device.Form.site'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'subjectKey',
        label: i18n.t('Page_Device.Form.subject'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'studyKeyOverride',
        label: i18n.t('Page_Device.Form.studyOverride'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'siteKeyOverride',
        label: i18n.t('Page_Device.Form.siteOverride'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'subjectKeyOverride',
        label: i18n.t('Page_Device.Form.subjectOverride'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
      },
      {
        name: 'timezone',
        label: i18n.t('Page_Device.Form.timezone'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'newUpdateReason',
        label: i18n.t('Page_Device.Form.newUpdateReason'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
        isEditOnly: true,
      },
      {
        name: 'bundleId',
        label: i18n.t('Page_Device.Form.bundleId'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'bundleVersion',
        label: i18n.t('Page_Device.Form.bundleVersion'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'os',
        label: i18n.t('Page_Device.Form.os'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'osVersion',
        label: i18n.t('Page_Device.Form.osVersion'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'phoneModel',
        label: i18n.t('Page_Device.Form.phoneModel'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'phoneManufacturer',
        label: i18n.t('Page_Device.Form.phoneManufacturer'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'createdBy',
        label: i18n.t('Page_Device.Form.createdBy'),
        type: DynamicFormFieldType.TEXT,
      },
      {
        name: 'createdOn',
        label: i18n.t('Page_Device.Form.createdOn'),
        type: DynamicFormFieldType.DATE,
      },
      {
        name: 'updatedBy',
        label: i18n.t('Page_Device.Form.updatedBy'),
        type: DynamicFormFieldType.TEXT,
      },
      {
        name: 'updatedOn',
        label: i18n.t('Page_Device.Form.updatedOn'),
        type: DynamicFormFieldType.DATE,
      },
      {
        name: 'updateReason',
        label: i18n.t('Page_Device.Form.updateReason'),
        type: DynamicFormFieldType.TEXT,
      },
    ],
    validationSchema: {
      key: Yup.string(),
      status: Yup.string(),
      subjectKey: Yup.string(),
      organizationKey: Yup.string(),
      studyKey: Yup.string(),
      siteKey: Yup.string(),
      deviceId: Yup.string(),
      os: Yup.string(),
      osVersion: Yup.string(),
      phoneModel: Yup.string(),
      phoneManufacturer: Yup.string(),
      bundleId: Yup.string(),
      bundleVersion: Yup.string(),
      timezone: Yup.string(),
      state: Yup.number(),
      appName: Yup.string(),
      uniqueId: Yup.string(),
      createdBy: Yup.string(),
      createdOn: Yup.string(),
      updatedBy: Yup.string(),
      updatedOn: Yup.string(),
      updateReason: Yup.string(),
      newUpdateReason: Yup.string().required(i18n.t('Form.genericRequired')),
      ...overrideValuesValidationSchema,
    },
    initialValues: {
      ...mapDeviceAndDeviceOverridesToFormValues(device, deviceOverrides),
    },
  };

  const updateDeviceOverrides = useMutation(
    updateDeviceOverridesMutateOptions(),
  );

  const handleSubmit = async (values: Record<string, any>) => {
    const deviceOverrideValues = mapFormValuesToDeviceOverrides(
      values as DeviceFormValues,
    );

    return updateDeviceOverrides
      .mutateAsync({
        deviceKey: device.key,
        updatedDeviceOverrideValues: deviceOverrideValues,
      })
      .then(async (updatedDeviceOverrides) => {
        const deviceQueryKey = deviceOverridesByDeviceKeyQueryOptions(
          device.key,
        ).queryKey;

        queryClient.setQueryData(deviceQueryKey, updatedDeviceOverrides);
        queryClient.invalidateQueries({
          queryKey: deviceByKeyQueryOptions(device.key).queryKey,
        });

        const updatedFormValues = mapDeviceAndDeviceOverridesToFormValues(
          device,
          updatedDeviceOverrides,
        );

        return updatedFormValues;
      })
      .catch((err) => {
        throw new Error(
          getErrorMessageOnEntityCreateUpdate(err, 'Page_Device'),
        );
      });
  };

  return (
    <VStack flexGrow={1} gap={'$4'} testID="device-details">
      <Card>
        <DynamicForm
          id="device-details"
          title={i18n.t('Page_Device.title')}
          formConfig={formConfig}
          onSave={handleSubmit}
          maxRows={Math.ceil(formConfig.fields.length / 2)}
        >
          {children}
        </DynamicForm>
      </Card>
    </VStack>
  );
}
